interface VoiceCommand {
  action: 'send' | 'receive' | 'balance' | 'history' | 'help' | 'unknown'
  blockchain?: 'ethereum' | 'solana'
  token?: string
  amount?: string
  recipient?: string
  reason?: string
  confidence: number
  originalText: string
  language: string
}

// OpenAI API configuration
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions'

// Fallback local NLP parser for when OpenAI is not available
const parseCommandLocally = (text: string, language: string): VoiceCommand => {
  const lowerText = text.toLowerCase()
  
  // Action detection patterns
  const sendPatterns = ['send', 'transfer', 'pay', 'give', 'enviar', 'pagar', 'envoyer', 'senden', 'inviare', 'отправить', '送る', '보내다', '发送', 'إرسال', 'भेजना']
  const balancePatterns = ['balance', 'wallet', 'funds', 'saldo', 'solde', 'guthaben', 'saldo', 'баланс', '残高', '잔액', '余额', 'رصيد', 'शेष']
  const historyPatterns = ['history', 'transactions', 'historial', 'historique', 'verlauf', 'storia', 'история', '履歴', '기록', '历史', 'تاريخ', 'इतिहास']
  
  let action: VoiceCommand['action'] = 'unknown'
  let confidence = 0.5
  
  // Detect action
  if (sendPatterns.some(pattern => lowerText.includes(pattern))) {
    action = 'send'
    confidence = 0.8
  } else if (balancePatterns.some(pattern => lowerText.includes(pattern))) {
    action = 'balance'
    confidence = 0.8
  } else if (historyPatterns.some(pattern => lowerText.includes(pattern))) {
    action = 'history'
    confidence = 0.8
  }
  
  // Extract blockchain
  let blockchain: 'ethereum' | 'solana' | undefined
  if (lowerText.includes('ethereum') || lowerText.includes('eth') || lowerText.includes('usdt') || lowerText.includes('usdc')) {
    blockchain = 'ethereum'
  } else if (lowerText.includes('solana') || lowerText.includes('sol')) {
    blockchain = 'solana'
  }
  
  // Extract token
  let token: string | undefined
  const tokenPatterns = {
    'bnb': 'BNB',
    'binance': 'BNB',
    'usdt': 'USDT',
    'usdc': 'USDC',
    'sol': 'SOL',
    'solana': 'SOL'
  }
  
  for (const [pattern, tokenSymbol] of Object.entries(tokenPatterns)) {
    if (lowerText.includes(pattern)) {
      token = tokenSymbol
      break
    }
  }
  
  // Extract amount using regex
  let amount: string | undefined
  const amountMatch = text.match(/([0-9]+(?:\.[0-9]+)?)/)
  if (amountMatch) {
    amount = amountMatch[1]
  }
  
  // Extract wallet address (simplified)
  let recipient: string | undefined
  const ethAddressMatch = text.match(/(0x[a-fA-F0-9]{40})/)
  const solAddressMatch = text.match(/([1-9A-HJ-NP-Za-km-z]{32,44})/)
  
  if (ethAddressMatch) {
    recipient = ethAddressMatch[1]
  } else if (solAddressMatch) {
    recipient = solAddressMatch[1]
  }
  
  return {
    action,
    blockchain,
    token,
    amount,
    recipient,
    confidence,
    originalText: text,
    language
  }
}

// Parse voice command using OpenAI GPT-4
const parseCommandWithOpenAI = async (text: string, language: string): Promise<VoiceCommand> => {
  if (!OPENAI_API_KEY) {
    throw new Error('OpenAI API key not configured')
  }

  const systemPrompt = `You are a voice payment assistant AI that parses natural language commands into structured JSON. 

Analyze the user's voice command and return a JSON object with these fields:
- action: "send" | "receive" | "balance" | "history" | "help" | "unknown"
- blockchain: "ethereum" | "solana" (if mentioned or can be inferred)
- token: token symbol like "ETH", "SOL", "USDC", "USDT" (if mentioned)
- amount: numeric amount as string (if mentioned)
- recipient: wallet address or contact name (if mentioned)
- reason: purpose of transaction (if mentioned)
- confidence: 0.0-1.0 confidence score

Supported languages: English, Spanish, French, German, Italian, Portuguese, Russian, Japanese, Korean, Chinese, Arabic, Hindi.

Examples:
"Send 0.1 BNB to 0xabc123" → {"action":"send","blockchain":"ethereum","token":"BNB","amount":"0.1","recipient":"0xabc123","confidence":0.9}
"What's my Solana balance?" → {"action":"balance","blockchain":"solana","confidence":0.9}
"Show transaction history" → {"action":"history","confidence":0.9}

Return only valid JSON, no explanations.`

  try {
    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-4',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: `Language: ${language}\nCommand: "${text}"` }
        ],
        temperature: 0.1,
        max_tokens: 200
      })
    })

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`)
    }

    const data = await response.json()
    const content = data.choices?.[0]?.message?.content

    if (!content) {
      throw new Error('No response from OpenAI')
    }

    // Parse the JSON response
    const parsed = JSON.parse(content)
    
    return {
      ...parsed,
      originalText: text,
      language,
      confidence: parsed.confidence || 0.5
    }
  } catch (error) {
    console.error('OpenAI parsing failed:', error)
    throw error
  }
}

// Main function to parse voice commands
export const parseVoiceCommand = async (text: string, language: string = 'en-US'): Promise<VoiceCommand> => {
  if (!text.trim()) {
    return {
      action: 'unknown',
      confidence: 0,
      originalText: text,
      language
    }
  }

  try {
    // Try OpenAI first if API key is available
    if (OPENAI_API_KEY) {
      return await parseCommandWithOpenAI(text, language)
    }
  } catch (error) {
    console.warn('OpenAI parsing failed, falling back to local parser:', error)
  }

  // Fallback to local parsing
  return parseCommandLocally(text, language)
}

// Generate response text based on parsed command
export const generateResponse = (command: VoiceCommand, language: string = 'en-US'): string => {
  const responses: Record<string, Record<string, string>> = {
    'en-US': {
      send: `I'll help you send ${command.amount || ''} ${command.token || 'tokens'} ${command.recipient ? `to ${command.recipient}` : ''}. Please confirm this transaction.`,
      balance: `Let me check your ${command.blockchain || 'wallet'} balance.`,
      history: 'Here is your transaction history.',
      help: 'I can help you send payments, check balances, and view transaction history. Just speak naturally!',
      unknown: 'I didn\'t understand that command. Try saying something like "send 0.1 BNB" or "check my balance".'
    },
    'es-ES': {
      send: `Te ayudo a enviar ${command.amount || ''} ${command.token || 'tokens'} ${command.recipient ? `a ${command.recipient}` : ''}. Por favor confirma esta transacción.`,
      balance: `Déjame revisar tu saldo de ${command.blockchain || 'billetera'}.`,
      history: 'Aquí está tu historial de transacciones.',
      help: 'Puedo ayudarte a enviar pagos, revisar saldos y ver el historial de transacciones. ¡Solo habla naturalmente!',
      unknown: 'No entendí ese comando. Intenta decir algo como "enviar 0.1 BNB" o "revisar mi saldo".'
    },
    'fr-FR': {
      send: `Je vais vous aider à envoyer ${command.amount || ''} ${command.token || 'tokens'} ${command.recipient ? `à ${command.recipient}` : ''}. Veuillez confirmer cette transaction.`,
      balance: `Laissez-moi vérifier votre solde ${command.blockchain || 'portefeuille'}.`,
      history: 'Voici votre historique de transactions.',
      help: 'Je peux vous aider à envoyer des paiements, vérifier les soldes et voir l\'historique des transactions. Parlez naturellement!',
      unknown: 'Je n\'ai pas compris cette commande. Essayez de dire quelque chose comme "envoyer 0.1 BNB" ou "vérifier mon solde".'
    }
  }

  const langCode = language.split('-')[0]
  const langResponses = responses[language] || responses[`${langCode}-${langCode.toUpperCase()}`] || responses['en-US']
  
  return langResponses[command.action] || langResponses.unknown
}

// Validate wallet address format
export const validateWalletAddress = (address: string, blockchain: 'ethereum' | 'solana'): boolean => {
  if (blockchain === 'ethereum') {
    return /^0x[a-fA-F0-9]{40}$/.test(address)
  } else if (blockchain === 'solana') {
    return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address)
  }
  return false
}

// Extract and validate amount
export const validateAmount = (amount: string): { isValid: boolean; value: number } => {
  const numValue = parseFloat(amount)
  return {
    isValid: !isNaN(numValue) && numValue > 0 && isFinite(numValue),
    value: numValue
  }
}

// Get supported tokens for blockchain
export const getSupportedTokens = (blockchain: 'ethereum' | 'solana'): string[] => {
  if (blockchain === 'ethereum') {
    return ['ETH', 'USDT', 'USDC', 'DAI', 'WETH']
  } else if (blockchain === 'solana') {
    return ['SOL', 'USDC', 'USDT', 'RAY', 'SRM']
  }
  return []
}